import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import GlobalStyle from "./styles/GlobalStyle";
import Letter from "./components/Letter";
import ScrollSequence from "./components/ScrollSequence";
import QRGate from "./components/QRGate";
import Dialogue from "./components/Dialogue";
import Lockbox from "./components/Lockbox";
import { COPY, detectLanguage } from "./config";

/*
 * STAGES
 *   letter    – the sealed letter, tap to open, break the seal
 *   walk      – scroll-driven walk down the modern street, through the
 *               portal (which lives in the video), into a century ago.
 *               The date readout flips as you pass through.
 *   qr1       – checkpoint scan at the basilica
 *   dialogue  – the villager, and the first paid clue (the drawing)
 *   qr2       – checkpoint scan past the Genoese gate
 *   lockbox   – the box: name + email → one-time code → dials →
 *               a photograph of the Citadel and nothing else
 *
 * Only "walk" scrolls. Everything else is a locked full-screen stage.
 *
 * NEXT: the Citadel. Another QR, then the Citadel woman, the telescope,
 * and the transport fork. Hook is onLockboxFinish below.
 *
 * Portal.js and HistoricalScene.js are out of this flow — the client's
 * video does the portal. Still on disk for a later checkpoint that may
 * want a code-driven transition.
 */

const SCROLLING_STAGES = ["walk"];

export default function App() {
  const [lang, setLang] = useState(detectLanguage);
  const [stage, setStage] = useState("letter");
  const copy = COPY[lang];

  useEffect(() => {
    const scrolling = SCROLLING_STAGES.includes(stage);
    document.body.dataset.locked = String(!scrolling);
    if (scrolling) window.scrollTo(0, 0);
    return () => {
      document.body.dataset.locked = "false";
    };
  }, [stage]);

  const toWalk = useCallback(() => setStage("walk"), []);
  const toQR1 = useCallback(() => setStage("qr1"), []);
  const toDialogue = useCallback(() => setStage("dialogue"), []);
  const toQR2 = useCallback(() => setStage("qr2"), []);
  const toLockbox = useCallback(() => setStage("lockbox"), []);

  const onLockboxFinish = useCallback(() => {
    /* CHECKPOINT 3 goes here — the Citadel. Deliberately empty: the
       player has a photograph and has to work out where it was taken.
       Nothing in the app should tell them. */
  }, []);

  return (
    <>
      <GlobalStyle />

      {stage === "letter" && (
        <>
          <LangBar>
            {Object.keys(COPY).map((code) => (
              <LangButton
                key={code}
                $active={code === lang}
                onClick={() => setLang(code)}
              >
                {COPY[code].label}
              </LangButton>
            ))}
          </LangBar>
          <Letter copy={copy} onSealBroken={toWalk} />
        </>
      )}

      {stage === "walk" && (
        <ScrollSequence copy={copy} lang={lang} onSequenceComplete={toQR1} />
      )}

      {stage === "qr1" && (
        <QRGate
          title={copy.qr1Title}
          body={copy.qr1Body}
          copy={copy}
          onScanned={toDialogue}
        />
      )}

      {stage === "dialogue" && (
        <Dialogue
          characterId="townsman"
          copy={copy}
          lang={lang}
          onFinish={toQR2}
        />
      )}

      {stage === "qr2" && (
        <QRGate
          title={copy.qr2Title}
          body={copy.qr2Body}
          copy={copy}
          onScanned={toLockbox}
        />
      )}

      {stage === "lockbox" && (
        <Lockbox copy={copy} onFinish={onLockboxFinish} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

const LangBar = styled.div`
  position: fixed;
  top: calc(1.4rem + env(safe-area-inset-top));
  right: 1.4rem;
  z-index: 60;
  display: flex;
  gap: 0.9rem;
`;

const LangButton = styled.button`
  font-size: 0.66rem;
  letter-spacing: 0.2em;
  color: var(--parchment);
  opacity: ${(p) => (p.$active ? 0.95 : 0.34)};
  border-bottom: 1px solid
    ${(p) => (p.$active ? "var(--brass)" : "transparent")};
  padding-bottom: 2px;
  transition: opacity 0.25s ease;
`;
